import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MdArrowBackIosNew, MdDateRange, MdNavigation, MdPriceChange, MdWork, MdWorkOutline } from 'react-icons/md';
import { IoMdClock } from 'react-icons/io';
import { useStateValue } from '../context/StateProvider';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import { doc, getDoc, getFirestore } from 'firebase/firestore';

const ProductDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const product = location.state?.product;
  const [{ user }] = useStateValue();
  const [hasApplied, setHasApplied] = useState(false);
  const firestore = getFirestore();

  useEffect(() => {
      const checkApplication = async () => {
          if (user && product) {
              try {
                  const applicationDocRef = doc(firestore, 'applications', `${user.username}-${product.id}`);
                  const applicationDoc = await getDoc(applicationDocRef);
                  setHasApplied(applicationDoc.exists());
              } catch (error) {
                  console.error('Error checking application:', error);
              }
          }
      };
      checkApplication();
  }, [user, product, firestore]);

  const handleApplyNow = () => {
      if (user) {
          if (hasApplied) {
              toast.info('You have already applied for this job.');
              return;
          }
          navigate('/upload-resume', { state: { product } });
      } else {
          toast.error('Before applying for a job, please Login/Registration your account');
          navigate('/Signin');
      }
  };

  const calculateDaysSincePosted = (postedDate) => {
    if (!postedDate) return "No posted date";
    const today = new Date();
    const postedDateObj = new Date(postedDate);
    if (isNaN(postedDateObj.getTime())) return "Invalid date";
    const timeDiff = today - postedDateObj;
    const daysSince = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    return daysSince >= 0 ? daysSince : "Future date";
  };

  useEffect(() => {
    console.log("Product Details: ", product); // Debugging line
  }, [product]);

  return (
    <section className="max-w-[1400px] mx-auto p-5  lg:mt-2 mb-10" >
      <Helmet>
        <title>Products Details - Nexify Infosystms</title>
        <meta name="description" content={product ? `Apply for the ${product.jobTitle} at ${product.companyName}. ${product.jobDescription}` : 'Find the latest job details here.'} />
        <meta name="keywords" content={product ? `${product.jobTitle}, ${product.companyName}, job, career, apply, jobs` : 'jobs, job listings, apply for jobs'} />
      </Helmet>
      <button
        onClick={() => navigate(-1)}
        className="mb-4 mt-2 px-4 py-2 text-textColor rounded-lg transition duration-300 flex items-center gap-1"
      >
        <MdArrowBackIosNew className='text-[12px]' /> Back
      </button>

      {product ? (
        <div className="flex flex-col md:flex-row gap-8 justify-center items-start">
          <div className="md:w-3/4 flex flex-col bg-white shadow-sx rounded-sx p-6 border border-gray-200">
            <h2 className="text-xl md:text-2xl font-semibold text-textColor mb-2">{product.jobTitle}</h2>
            <p className="text-gray-700 text-sm mb-1">{product.companyName}</p>
            <hr className="my-2 border-gray-300" />
            <ul className="text-gray-600 text-xs mb-4 space-y-2">
              <li className="flex items-center mb-10">
                <MdNavigation className="mr-2 text-[20px]" />
                <div className="flex flex-col text-gray-500 text-[15px]">
                  <span className="font-semibold">LOCATION:</span>
                  <span className='text-[13px]'>{product.location}</span>
                </div>
              </li>
              <div className='flex flex-col md:flex-row justify-between'>
                <li className="flex items-center mb-2">
                  <MdPriceChange className="mr-2 text-[20px]" />
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-500 text-[15px]">SALARY:</span>
                    <span className='text-[13px]'>₹{product.salary}/{product.salaryType}</span>
                  </div>
                </li>
                <li className="flex items-center mb-2">
                  <MdWorkOutline className="mr-2 text-[20px]" />
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-500 text-[15px]">EXPERIENCE:</span>
                    <span className='text-[13px]'>{product.experienceRequired} years</span>
                  </div>
                </li>
                <li className="flex items-center mb-2">
                  <MdWork className="mr-2 text-[20px]" />
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-500 text-[15px]">JOB TYPE:</span>
                    <span className='text-[13px]'>{product.jobType}</span>
                  </div>
                </li>
                <li className="flex items-center mb-2">
                  <MdDateRange className="mr-2 text-[20px]" />
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-500 text-[15px]">APPLICATION DEADLINE:</span>
                    <span className='text-[13px]'>{product.applicationDeadline}</span>
                  </div>
                </li>
              </div>
            </ul>
            <div className="flex items-center mt-5">
              <IoMdClock className="mr-2 text-[20px]" />
              <div className="flex flex-col">
                <span className="font-semibold text-gray-500 text-[15px]">POSTED:</span>
                <span className='text-[13px]'> {calculateDaysSincePosted(product.postedDate)} days ago</span>
              </div>
            </div>

            <h3 className="text-lg font-semibold mt-4 mb-2">Job Description</h3>
            <div className="text-gray-600 leading-relaxed mb-4 text-[14px]" dangerouslySetInnerHTML={{ __html: product.jobDescription }} />

            <button
              onClick={handleApplyNow}
              className="mt-4 w-full bg-[#005eb8] text-white font-semibold py-2 rounded-sx hover:bg-[#005eb8] transition duration-300"
            >
              Apply Now
            </button>
          </div>
        </div>
      ) : (
        <p className="text-center text-gray-500">No job details available.</p>
      )}
    </section>
  );
};

export default ProductDetails;
